const THUMBNAIL_SIZE = 512;

const cropSquare = (width: number, height: number) => {
    const side = Math.min(width, height);
    return {
        side,
        sx: (width - side) / 2,
        sy: (height - side) / 2,
    };
};

const canvasToBlob = async (canvas: HTMLCanvasElement) =>
    new Promise<Blob | null>(resolve =>
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.82),
    );

export const createImageThumbnail = async (file: File) => {
    const url = URL.createObjectURL(file);

    try {
        const img = new Image();
        img.src = url;

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Image load failed'));
        });

        const canvas = document.createElement('canvas');
        canvas.width = THUMBNAIL_SIZE;
        canvas.height = THUMBNAIL_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        const { side, sx, sy } = cropSquare(img.width, img.height);
        ctx.drawImage(
            img,
            sx,
            sy,
            side,
            side,
            0,
            0,
            THUMBNAIL_SIZE,
            THUMBNAIL_SIZE,
        );

        return await canvasToBlob(canvas);
    } catch (error) {
        console.warn('Thumbnail generation failed', error);
        return null;
    } finally {
        URL.revokeObjectURL(url);
    }
};

export const createVideoThumbnail = async (file: File) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;

    try {
        await new Promise<void>((resolve, reject) => {
            const cleanup = () => {
                video.onloadedmetadata = null;
                video.onseeked = null;
                video.onerror = null;
            };

            video.onloadedmetadata = () => {
                video.currentTime = Math.min(0.1, video.duration || 0.1);
            };

            video.onseeked = () => {
                cleanup();
                resolve();
            };

            video.onerror = () => {
                cleanup();
                reject(new Error('Video load failed'));
            };
        });

        if (!video.videoWidth || !video.videoHeight) return null;

        const canvas = document.createElement('canvas');
        canvas.width = THUMBNAIL_SIZE;
        canvas.height = THUMBNAIL_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        const { side, sx, sy } = cropSquare(
            video.videoWidth,
            video.videoHeight,
        );
        ctx.drawImage(
            video,
            sx,
            sy,
            side,
            side,
            0,
            0,
            THUMBNAIL_SIZE,
            THUMBNAIL_SIZE,
        );

        return await canvasToBlob(canvas);
    } catch (error) {
        console.warn('Video thumbnail generation failed', error);
        return null;
    } finally {
        URL.revokeObjectURL(url);
    }
};
